import { asBool, asDate, asInt, asString, asStringList, type Json } from './json';
import type { AnswerLeniency } from './studyQuestion';
import type { VoiceGender } from '@/lib/audioUrl';

// Port of UserSettingsRow from lib/repositories/user_settings_repository.dart —
// the server-side representation of the `user_settings` table. Both the
// app-preferences (settings store) and the profile (useUserProfile) read
// from this single row.
export interface UserSettingsRow {
  showRomanization: boolean;
  freeTextEnabled: boolean;
  uiLocale?: string;
  sessionSize: number;
  displayName?: string;
  avatarColor?: number;
  nativeLanguage?: string;
  learningLanguage?: string;
  onboardedAt?: Date;
  typingDrillSourceLang?: string;
  typingDrillTargetLang?: string;
  autoCacheDecoys: boolean;
  favouriteLanguages: string[];
  answerLeniency: AnswerLeniency;
  /** Auto-warm the whole decoy cache as soon as the app opens (account-wide). */
  autoWarmOnOpen: boolean;
  /** Which TTS voice to play, account-wide (migration 134). */
  voiceGender: VoiceGender;
  // Optional columns, parsed WITHOUT a default: null = NULL on the server
  // ("not set on the account yet"), undefined = the column doesn't exist on
  // this server. The sync keeps the local value in both cases (contract §3).
  /** "I teach" (migration 160). */
  teachesOptIn?: boolean | null;
  /** "Extra practice on my difficulties" (migration 175, NOT NULL default true). */
  difficultySupport?: boolean | null;
  /** New words per session, 0–100 (migration 181). */
  newWordsPerSession?: number | null;
  /** Appearance (migration 181). */
  themeMode?: 'system' | 'light' | 'dark' | null;
  /** "Show Teaching in the navigation" (migration 181). */
  showTeachingTab?: boolean | null;
  /** Raw server values by column for the sync merge (only the columns read). */
  raw: Record<string, unknown>;
}

export function userSettingsRowFromJson(json: Json): UserSettingsRow {
  return {
    showRomanization: asBool(json['show_romanization']) ?? true,
    freeTextEnabled: asBool(json['free_text_enabled']) ?? true,
    uiLocale: asString(json['ui_locale']),
    sessionSize: asInt(json['session_size']) ?? 20,
    displayName: asString(json['display_name']),
    avatarColor: asInt(json['avatar_color']),
    nativeLanguage: asString(json['native_language']),
    learningLanguage: asString(json['learning_language']),
    onboardedAt: asDate(json['onboarded_at']),
    typingDrillSourceLang: asString(json['typing_drill_source_lang']),
    typingDrillTargetLang: asString(json['typing_drill_target_lang']),
    autoCacheDecoys: asBool(json['auto_cache_decoys']) ?? true,
    favouriteLanguages: asStringList(json['favourite_languages']),
    answerLeniency: parseAnswerLeniency(asString(json['answer_leniency'])),
    autoWarmOnOpen: asBool(json['auto_warm_on_open']) ?? false,
    voiceGender: asString(json['voice_gender']) === 'm' ? 'm' : 'f',
    teachesOptIn: optBool(json, 'teaches_opt_in'),
    difficultySupport: optBool(json, 'difficulty_support'),
    newWordsPerSession: optNewWords(json),
    themeMode: optTheme(json),
    showTeachingTab: optBool(json, 'show_teaching_tab'),
    raw: { ...json },
  };
}

// undefined = column not in the row (not selected / not on this server);
// null = NULL or an invalid value.
function optBool(json: Json, col: string): boolean | null | undefined {
  if (!(col in json)) return undefined;
  const v = json[col];
  return typeof v === 'boolean' ? v : null;
}
function optNewWords(json: Json): number | null | undefined {
  if (!('new_words_per_session' in json)) return undefined;
  const v = json['new_words_per_session'];
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 100 ? v : null;
}
function optTheme(json: Json): 'system' | 'light' | 'dark' | null | undefined {
  if (!('theme_mode' in json)) return undefined;
  const v = json['theme_mode'];
  return v === 'system' || v === 'light' || v === 'dark' ? v : null;
}

/** Validates the stored leniency string, defaulting to the strictest mode. */
export function parseAnswerLeniency(raw: string | undefined): AnswerLeniency {
  return raw === 'accents' || raw === 'typos' ? raw : 'exact';
}

/** Core columns selected by UserSettingsRepository — every server has them.
 *  Kept in sync with the parser. */
export const USER_SETTINGS_COLUMNS =
  'show_romanization, free_text_enabled, ui_locale, session_size, ' +
  'display_name, avatar_color, native_language, learning_language, ' +
  'onboarded_at, typing_drill_source_lang, typing_drill_target_lang, ' +
  'auto_cache_decoys, favourite_languages, answer_leniency, auto_warm_on_open, ' +
  'voice_gender';


/** Live since 160 / 175, read defensively (contract §6). */
export const USER_SETTINGS_LIVE_OPTIONAL_COLUMNS = 'teaches_opt_in, difficulty_support';

/** Added by db/migration_181_settings_sync.sql (draft): missing → device-local. */
export const USER_SETTINGS_V181_COLUMNS = 'new_words_per_session, theme_mode, show_teaching_tab';
